import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FiHeart, FiCalendar, FiMapPin, FiSun, FiStar } from 'react-icons/fi';
import coverImage from '../assets/cover.jpg';
import './HeroSection.css';

const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { scrollY } = useScroll();
  
  // Parallax effects
  const y1 = useTransform(scrollY, [0, 500], [0, 150]);
  const y2 = useTransform(scrollY, [0, 500], [0, -150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: (e.clientY / window.innerHeight) * 2 - 1
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const floatingElements = [
    { id: 1, icon: FiHeart, delay: 0, duration: 6, color: '#B8860B' },
    { id: 2, icon: FiSun, delay: 2, duration: 8, color: '#CD853F' },
    { id: 3, icon: FiStar, delay: 4, duration: 7, color: '#8B4513' },
    { id: 4, icon: FiHeart, delay: 1, duration: 9, color: '#DAA520' },
    { id: 5, icon: '🪔', delay: 3, duration: 5, color: '#B8860B', isEmoji: true },
    { id: 6, icon: '🌺', delay: 5, duration: 6, color: '#CD853F', isEmoji: true },
    { id: 7, icon: FiStar, delay: 1.5, duration: 7, color: '#8B4513' },
    { id: 8, icon: '🕉️', delay: 4.5, duration: 8, color: '#B8860B', isEmoji: true }
  ];


  const heroStyle = {
    backgroundImage: `url(${coverImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: window.innerWidth > 768 ? 'fixed' : 'scroll'
  };

  return (
    <section id="home" className="hero-section" style={heroStyle}>
      {/* Background with parallax */}
      <div className="hero-background">
        <motion.div 
          className="bg-gradient-1"
          style={{ y: y1 }}
        />
        <motion.div 
          className="bg-gradient-2"
          style={{ y: y2 }}
        />
        
        {/* Floating Kerala elements */}
        {floatingElements.map((element) => (
          <motion.div
            key={element.id}
            className="floating-element"
            animate={{
              y: [-20, 20, -20],
              x: [-10, 10, -10],
              rotate: [0, 5, -5, 0],
            }}
            transition={{
              duration: element.duration,
              repeat: Infinity,
              delay: element.delay,
              ease: "easeInOut"
            }}
            style={{
              transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * 20}px)`,
              color: element.color
            }}
          >
            {element.isEmoji ? (
              <span className="emoji-element">{element.icon}</span>
            ) : (
              <element.icon />
            )}
          </motion.div>
        ))}
      </div>

      {/* Main content */}
      <motion.div 
        className="hero-content"
        style={{ opacity }}
      >
        <motion.div
          className="hero-text"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <motion.div 
            className="couple-names"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <motion.h1
              className="bride-name"
              whileHover={{ scale: 1.05, color: "#e91e63" }}
              transition={{ duration: 0.3 }}
            >
              Aiswarya Aravind
            </motion.h1>
            
            <motion.div 
              className="ampersand"
              animate={{ 
                rotate: [0, 5, -5, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <FiHeart />
            </motion.div>
            
            <motion.h1
              className="groom-name"
              whileHover={{ scale: 1.05, color: "#d4a373" }}
              transition={{ duration: 0.3 }}
            >
              Deepak Raj
            </motion.h1>
          </motion.div>

          <div className="wedding-info">
            <div 
              className="info-item info-item-glass"
            >
              <FiCalendar className="info-icon" />
              <span>October 19, 2025</span>
            </div>
            <div 
              className="info-item location-item info-item-glass" 
              onClick={() => window.open('https://maps.app.goo.gl/zJgTHgpNgNkWqi3X7', '_blank')}
            >
              <FiMapPin className="info-icon" />
              <span>Aiswarya Auditorium</span>
            </div>
          </div>

          <motion.p
            className="invitation-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            Together with their families, they joyfully invite you to celebrate their union and the beginning of their forever journey
          </motion.p>

          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
          >
            <motion.button
              className="cta-button primary"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('details').scrollIntoView({ behavior: 'smooth' })}
            >
              View Details
            </motion.button>
            <motion.button
              className="cta-button secondary"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('gallery').scrollIntoView({ behavior: 'smooth' })}
            >
              View Gallery
            </motion.button>
          </motion.div>
        </motion.div>
        
        {/* Explicit barrier to prevent any elements from appearing below */}
        <div style={{ 
          position: 'absolute', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          height: '150px',
          background: 'transparent',
          zIndex: 5000,
          pointerEvents: 'none'
        }} />
      </motion.div>

      {/* Explicitly block any potential unwanted elements */}
      <div style={{ display: 'none' }} className="hidden-block" />

      {/* Decorative elements */}
      <div className="decorative-elements">
        <motion.div
          className="decoration decoration-1"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="decoration decoration-2"
          animate={{
            rotate: [360, 0],
            scale: [1, 0.8, 1]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>
    </section>
  );
};

export default HeroSection;
